import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDialogFocus } from "@hybrid-iptv/ui";
import { launcherAvailable } from "../launcher/launcherApi.js";
import { useLauncher } from "../launcher/launcherStore.js";
import { confirmExitHandler, leaveApp } from "../api/nativeBack.js";
import { brand } from "../brand.js";

/**
 * "Are you sure you want to leave <app name>?"
 *
 * WHY. Back at the first screen is the one press that cannot be undone, and
 * until now it just happened (owner, 2026-09-22: "I want to add a leaving the
 * app pop-up confirmation so no more leaving by mistake"). On a remote, Back
 * is also the key people hit hardest and most often — to close a card, to come
 * out of full screen — so the press that ends the session looks exactly like
 * the fifty before it.
 *
 * HOW IT REACHES HERE. Android's Back goes to the activity, not to JavaScript.
 * MainActivity reads the depth the page has been reporting, and when there is
 * nothing left to undo it calls the handler this component installs instead of
 * finishing. Nothing leaves the app until this dialog says so.
 *
 * BACK WHILE IT IS OPEN MEANS STAY, and that is not special-cased anywhere: the
 * dialog is a back layer like any other, so opening it puts the depth at one
 * and the next Back closes it the way Back closes a card. The rule that Back
 * undoes the last thing that happened covers the dialog that asks about Back.
 *
 * Focus is trapped by `useDialogFocus` and starts on Stay, so a stray Enter —
 * or a remote with a sticky key — cannot confirm the thing being guarded
 * against.
 */
export function ExitConfirm() {
  const [open, setOpen] = useState(false);
  // Read through refs, so the handler below is installed ONCE. With `navigate` as a dependency it
  // was taken down and put back on every page change (the router makes a new `navigate` per
  // location), telling the native side "no handler" for a moment each time a Back could be
  // deciding (2026-10-20).
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const dialogRef = useRef<HTMLDivElement>(null);
  const stayRef = useRef<HTMLButtonElement>(null);

  const stay = useCallback(() => setOpen(false), []);
  useDialogFocus({ open, onClose: stay, rootRef: dialogRef });

  // Native asks; this is the only thing that opens it.
  //
  // In launcher mode SliqueTV IS the home screen, so there is nothing to leave to (owner,
  // 2026-10-20: "back or home to the home launcher screen"). Back with nothing left to undo goes
  // to the top of Home instead: Home from another page, the banner's first button from further
  // down Home. The box's own Home button still leaves, as it always does.
  useEffect(
    () =>
      confirmExitHandler(() => {
        if (!(useLauncher.getState().enabled && launcherAvailable())) {
          setOpen(true);
          return;
        }
        // Home reads the marker and moves its own focus (HomePage, launcherTop), so its
        // remembered place and the ring stay together.
        navigateRef.current("/", { replace: pathRef.current === "/", state: { launcherTop: Date.now() } });
      }),
    [],
  );

  // Start on Stay rather than whatever happens to be first in the document.
  useEffect(() => {
    if (open) stayRef.current?.focus();
  }, [open]);

  if (!open) return null;

  // Left/Right between the two buttons: a TV remote's D-pad, not Tab.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const buttons = Array.from(dialogRef.current?.querySelectorAll("button") ?? []);
    if (buttons.length === 0) return;
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowRight" ? 1 : -1;
    const next = buttons[(Math.max(at, 0) + step + buttons.length) % buttons.length];
    next?.focus();
    event.preventDefault();
  };

  return (
    // No click-to-dismiss on the backdrop: the whole point is a deliberate
    // answer, and on a remote there is no stray click to dismiss it with
    // anyway.
    <div className="epg-program-detail-overlay exit-confirm-overlay">
      <div
        ref={dialogRef}
        className="epg-program-detail exit-confirm"
        role="alertdialog"
        aria-labelledby="exit-confirm-title"
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 className="epg-detail-title" id="exit-confirm-title">
          Are you sure you want to leave {brand().appName}?
        </h2>
        <div className="exit-confirm__actions">
          <button ref={stayRef} type="button" className="exit-confirm__stay" onClick={stay}>
            Stay
          </button>
          <button type="button" className="exit-confirm__leave" onClick={() => leaveApp()}>
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}
